import type { EngineLayer } from '@mighty-map/map-engine';
import { useMemo } from 'react';
import { create } from 'zustand';
import { mergeAdded, type AddedLayer } from '../../lib/data-passport/added';
import { useMapStore } from '../../state/mapStore';

/**
 * Layers Data Passport put on the map from another theme. Kept apart from the panel's own state so the map can read
 * it without loading the tool: the shell's map view passes its theme layers through `useWithAddedLayers`.
 */
interface AddedLayersState {
  added: AddedLayer[];
  add: (layer: AddedLayer) => void;
  remove: (layerId: string) => void;
}

export const useAddedLayers = create<AddedLayersState>()((set, get) => ({
  added: [],
  add: (layer) => set({ added: [...get().added.filter((a) => a.layerId !== layer.layerId), layer] }),
  remove: (layerId) => set({ added: get().added.filter((a) => a.layerId !== layerId) }),
}));

/** The theme's engine layers with Data Passport's additions on top (the same array when there are none). */
export function useWithAddedLayers(themeLayers: EngineLayer[]): EngineLayer[] {
  const added = useAddedLayers((s) => s.added);
  const layers = useMapStore((s) => s.layers);
  const manifest = useMapStore((s) => s.manifest);
  return useMemo(() => mergeAdded(themeLayers, added, layers, manifest), [themeLayers, added, layers, manifest]);
}
